import { IntegrationPackages } from "./IntegrationPackages";
import { useEsmAccess } from "./FeatureAccess";
import { StandaloneFormData } from "./StandaloneFormData";
import { FormConfigurationSession } from "@proof/sdk";
import { IntegrationAccess } from "./IntegrationAccess";
import { useEffect, useState } from "react";
import type { Project, Repository, Revision } from "@proof/core";
import { summarizeProject, type ProjectCatalogPage } from "@proof/core";
import { Control } from "./components";
import { Integrations } from "./Integrations";
import { FormConfigurationEditor } from "@proof/authoring";
import { EsmProjectIntegration } from "./EsmProjectIntegration";

/**
 * Integrations live in the shared library, not inside one project. Choose the
 * project and, optionally, a saved version to refine what each integration
 * (embeds, handoff, API, data transforms) targets.
 */
export function LibraryIntegrations({
  projects,
  repository,
  tenantId,
  initialProjectId,
  onImport,
  notify,
}: {
  projects: Project[];
  repository: Repository;
  tenantId: string;
  initialProjectId?: string;
  onImport: (projects: Project[]) => void | Promise<void>;
  notify: (message: string) => void;
}) {
  const esmAllowed = useEsmAccess();
  const [formDefinition, setFormDefinition] = useState(
    () => new FormConfigurationSession().value,
  );
  const [access, setAccess] = useState(false);
  const [esmEditor, setEsmEditor] = useState(false);
  const [standalone, setStandalone] = useState(
    () => projects.length === 0 && !repository.catalog,
  );
  const [projectId, setProjectId] = useState(
    () => initialProjectId || projects[0]?.id || "",
  );
  const [revisions, setRevisions] = useState<Revision[]>([]),
    [revisionId, setRevisionId] = useState("");
  const [choices, setChoices] = useState<ProjectCatalogPage>({
    items: projects.map(summarizeProject),
    total: projects.length,
    offset: 0,
    limit: 50,
  });
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [selectedProject, setSelectedProject] = useState<Project>();
  const [projectBusy, setProjectBusy] = useState(false);
  const [projectError, setProjectError] = useState("");
  useEffect(() => {
    if (initialProjectId) setProjectId(initialProjectId);
  }, [initialProjectId]);
  useEffect(() => {
    if (!repository.catalog) {
      setChoices({
        items: projects.map(summarizeProject),
        total: projects.length,
        offset: 0,
        limit: 50,
      });
      return;
    }
    let active = true;
    setCatalogBusy(true);
    setCatalogError("");
    const timer = setTimeout(() => {
      void repository.catalog!(tenantId, {
        status: "all",
        limit: 50,
        offset,
        search,
      })
        .then((page) => {
          if (!active) return;
          setChoices(page);
          if (page.offset !== offset) setOffset(page.offset);
          setProjectId((value) => value || page.items[0]?.id || "");
          if (!page.total && !search) setStandalone(true);
        })
        .catch((e) => {
          if (active) setCatalogError((e as Error).message);
        })
        .finally(() => {
          if (active) setCatalogBusy(false);
        });
    }, 150);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [repository, tenantId, projects, offset, search]);
  useEffect(() => {
    let active = true;
    setProjectError("");
    const cached = projects.find((project) => project.id === projectId);
    if (cached) {
      setSelectedProject(cached);
      setProjectBusy(false);
      return;
    }
    if (!projectId) {
      setSelectedProject(undefined);
      setProjectBusy(false);
      return;
    }
    setProjectBusy(true);
    void repository
      .get(tenantId, projectId)
      .then((project) => {
        if (active) {
          setSelectedProject(project);
          if (!project) setProjectError("The selected project is unavailable.");
        }
      })
      .catch((e) => {
        if (active) setProjectError((e as Error).message);
      })
      .finally(() => {
        if (active) setProjectBusy(false);
      });
    return () => {
      active = false;
    };
  }, [projectId, projects, repository, tenantId]);
  useEffect(() => {
    if (!projectId && projects[0]) setProjectId(projects[0].id);
  }, [projects, projectId]);
  useEffect(() => {
    let active = true;
    setRevisions([]);
    setRevisionId("");
    if (!projectId) return;
    void repository
      .history(tenantId, projectId)
      .then((rows) => active && setRevisions(rows))
      .catch(() => active && setRevisions([]));
    return () => {
      active = false;
    };
  }, [projectId, repository, tenantId]);
  const current =
    selectedProject?.id === projectId
      ? selectedProject
      : projects.find((row) => row.id === projectId);
  const revision = revisions.find((row) => row.id === revisionId);
  const project = revision?.definition || current;
  const scope = (
    <section
      className="panel padded integration-scope"
      aria-label="Integration scope"
    >
      <div className="control-grid">
        <Control label="Project">
          <select
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
          >
            {!choices.items.length && !current && (
              <option value="">
                {catalogBusy ? "Loading projects…" : "No matching projects"}
              </option>
            )}
            {current && !choices.items.some((row) => row.id === current.id) && (
              <option value={current.id}>{current.name}</option>
            )}
            {choices.items.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </Control>
        <Control label="Version">
          <select
            value={revisionId}
            disabled={!revisions.length}
            onChange={(event) => setRevisionId(event.target.value)}
          >
            <option value="">Current draft</option>
            {revisions.map((row) => (
              <option key={row.id} value={row.id}>
                Revision {row.revision}
                {row.reason ? ` · ${row.reason}` : ""} ·{" "}
                {new Date(row.at).toLocaleDateString()}
              </option>
            ))}
          </select>
        </Control>
      </div>
      {repository.catalog && (
        <>
          <Control label="Search integration projects">
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
            />
          </Control>
          <div className="button-row" aria-label="Integration project pages">
            <button
              disabled={catalogBusy || choices.offset === 0}
              onClick={() =>
                setOffset(Math.max(0, choices.offset - choices.limit))
              }
            >
              Previous project choices
            </button>
            <span>
              {choices.total
                ? `${choices.offset + 1}–${choices.offset + choices.items.length} of ${choices.total} projects`
                : "No matching projects"}
            </span>
            <button
              disabled={
                catalogBusy ||
                choices.offset + choices.items.length >= choices.total
              }
              onClick={() => setOffset(choices.offset + choices.limit)}
            >
              Next project choices
            </button>
          </div>
        </>
      )}
      {catalogError && <p role="alert">{catalogError}</p>}
      {projectError && <p role="alert">{projectError}</p>}
      <p className="muted">
        Pick the integration type below. Embeds, handoff and exports use the
        project and version chosen here.
      </p>
    </section>
  );
  return (
    <div className="library-integrations">
      <IntegrationPackages />
      <div className="section-tabs">
        <button
          className={!access && !esmEditor && standalone ? "active" : ""}
          onClick={() => {
            setAccess(false);
            setEsmEditor(false);
            setStandalone(true);
          }}
        >
          Form configuration plugin
        </button>
        <button
          className={!access && !esmEditor && !standalone ? "active" : ""}
          onClick={() => {
            setAccess(false);
            setStandalone(false);
            setEsmEditor(false);
          }}
        >
          Project integrations
        </button>
        {esmAllowed && (
          <button
            className={!access && esmEditor ? "active" : ""}
            onClick={() => {
              setAccess(false);
              setEsmEditor(true);
            }}
          >
            ESM project editor
          </button>
        )}
        <button
          className={access ? "active" : ""}
          onClick={() => setAccess(true)}
        >
          Access &amp; tokens
        </button>
      </div>
      {access ? (
        <IntegrationAccess notify={notify} />
      ) : esmEditor && esmAllowed ? (
        <EsmProjectIntegration />
      ) : standalone ? (
        <>
          <p>
            Build a reusable form here without a project. External data
            bindings, a live form preview, and drop-in setup code follow the
            editor.
          </p>
          <FormConfigurationEditor
            esmEnabled={esmAllowed}
            value={formDefinition}
            onChange={(definition) => setFormDefinition(definition)}
          />
          <StandaloneFormData definition={formDefinition} />
          <details className="panel padded">
            <summary>Embed in your host application</summary>
            <p>
              Load the widget bundle and supply standalone configuration JSON.
              Saving returns your definition without making API requests. Your
              host save callback owns persistence. If it calls the Proof Studio
              API, keep its client secret on your backend and configure B2B
              access for the permissions it needs.
            </p>
            <pre>
              {esmAllowed
                ? `<script src="${window.location.origin}/embed.js"></script>
<proof-form-configuration id="configuration"></proof-form-configuration>
<script>
const editor = document.getElementById("configuration");
editor.value = hostDataset;
// For a dataset with multiple projects:
// editor.options = { projectId: 7 };
editor.onSave = async ({ definition, esm }) => {
  await saveThroughYourHost(esm);
};
</script>`
                : `<script src="${window.location.origin}/embed.js"></script>
<proof-form-configuration id="configuration"></proof-form-configuration>
<script>
const editor = document.getElementById("configuration");
editor.value = hostDefinition;
editor.onSave = async ({ definition }) => saveThroughYourHost(definition);
</script>`}
            </pre>
          </details>
        </>
      ) : project ? (
        <Integrations
          key={`${project.tenantId}:${project.id}:${revisionId}`}
          project={project}
          onImport={onImport}
          notify={notify}
          scope={scope}
          onAccess={() => setAccess(true)}
        />
      ) : (
        <>
          {scope}
          <p>
            {projectBusy || catalogBusy
              ? "Loading selected project…"
              : "Create or import a project to configure document embeds. Access & tokens remains available without a project."}
          </p>
        </>
      )}
    </div>
  );
}
